/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { OrderTemplate } from '@msdyn365-commerce-modules/retail-actions';
import { ICoreContext } from '@msdyn365-commerce/core';
import { AuditableOperation, BusinessPartnerUser } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

export interface IAuditMessageResources {
    orderTemplateCreatedText: string;
    orderTemplateSharedText: string;
    orderTemplateModifiedText: string;
    orderTemplateYouText: string;
    orderTemplateForText: string;
    orderTemplateByText?: string;
}

/**
 * Identifies the signed-in user for the two audit message personas.
 *
 * Both ids come from the request context rather than from `BusinessPartnerUser`:
 *  - `StaffId` is only populated on `BusinessPartnerUser` in the B2B seller channel, and in an
 *    on-behalf-of session the entity describes the impersonated contact rather than the signed-in
 *    account manager.
 *  - Reading the contact person id from the request context as well keeps the audit message
 *    independent of when the `businessPartnerUser` data action resolves. The entity is only used as
 *    a fallback for the legacy (non B2B contact) flow, where the request context does not carry it.
 *
 * @param context - The module core context.
 * @param businessPartnerUser - The current business partner user, when it is available.
 * @returns The staff id and contact person id of the signed-in user.
 */
const getCurrentUserIds = (
    context: ICoreContext | undefined,
    businessPartnerUser: BusinessPartnerUser | undefined
): { currentStaffId?: string; currentContactPersonId?: string } => {
    const user = context?.request?.user;

    return {
        currentStaffId: user?.staffId,
        currentContactPersonId: user?.contactPersonId ?? businessPartnerUser?.ContactPersonId
    };
};

/**
 * Calculates the {operator} text from an AuditableOperation and the current user.
 *
 * Logic follows two persona paths:
 *  - Account manager (the signed-in user has a staff id)
 *  - B2B buyer     (the signed-in user has only a contact person id)
 *
 * @param operation - The audit operation (CreatedBy / UpdatedBy / SharedBy).
 * @param businessPartnerUser - The current business partner user, used only as a fallback source for
 * the contact person id (see {@link getCurrentUserIds}).
 * @param resources - Optional localised "You" and "for" tokens.
 * @param context - The module core context, used to identify the signed-in user.
 */
export const getOperatorText = (
    operation: AuditableOperation | undefined,
    businessPartnerUser: BusinessPartnerUser | undefined,
    resources?: Pick<IAuditMessageResources, 'orderTemplateYouText' | 'orderTemplateForText'>,
    context?: ICoreContext
): string => {
    if (!operation) {
        return '';
    }

    if (!resources) {
        return operation.StaffName || operation.ContactPersonFirstName || '';
    }

    const { orderTemplateYouText, orderTemplateForText } = resources;
    const { currentStaffId, currentContactPersonId } = getCurrentUserIds(context, businessPartnerUser);

    // Case 1: Current user is an account manager (has a staff id)
    if (currentStaffId) {
        if (operation.StaffId && currentStaffId === operation.StaffId) {
            // "You for {ContactPersonFirstName}"
            return operation.ContactPersonFirstName
                ? `${orderTemplateYouText} ${orderTemplateForText} ${operation.ContactPersonFirstName}`
                : orderTemplateYouText;
        }

        if (!operation.StaffId) {
            // "{ContactPersonFirstName}"
            return operation.ContactPersonFirstName || '';
        }

        // "{EmployeeName} for {ContactPersonFirstName}"
        return operation.ContactPersonFirstName
            ? `${operation.StaffName || ''} ${orderTemplateForText} ${operation.ContactPersonFirstName}`
            : operation.StaffName || '';
    }

    // Case 2: Current user is a B2B buyer
    if (currentContactPersonId && currentContactPersonId === operation.ContactPersonId) {
        if (!operation.StaffId) {
            // "you"
            return orderTemplateYouText;
        }

        // "{EmployeeName} for you"
        return `${operation.StaffName || ''} ${orderTemplateForText} ${orderTemplateYouText}`;
    }

    // Fallback: "{ContactPersonFirstName}"
    return operation.ContactPersonFirstName || '';
};

export const getSharedMessageComponent = (
    orderTemplate: OrderTemplate | undefined,
    businessPartnerUser: BusinessPartnerUser | undefined,
    resources: IAuditMessageResources,
    formatOperationDate?: (d: Date) => string,
    context?: ICoreContext
): React.ReactNode | null => {
    if (!orderTemplate) {
        return null;
    }

    const { productList } = orderTemplate;

    if (!productList?.IsShared) {
        return null;
    }

    const sharedOperation = productList.SharedBy;
    const sharedDate = sharedOperation?.OperationDateTime;
    const formattedDate = sharedDate && formatOperationDate ? ` ${formatOperationDate(sharedDate)}` : '';
    const byText = resources.orderTemplateByText ? ` ${resources.orderTemplateByText}` : '';
    const actionText = `${resources.orderTemplateSharedText}${formattedDate}${byText}`;
    const operatorText = getOperatorText(sharedOperation, businessPartnerUser, resources, context);

    return (
        <div className='organization-order-template shared'>
            <span className='creator'>
                {actionText}
                {operatorText && <span>{` ${operatorText}`}</span>}
            </span>
        </div>
    );
};

export const getCreatedOrModifiedMessageComponent = (
    orderTemplate: OrderTemplate | undefined,
    businessPartnerUser: BusinessPartnerUser | undefined,
    resources: IAuditMessageResources,
    formatOperationDate?: (d: Date) => string,
    context?: ICoreContext
): React.ReactNode | null => {
    if (!orderTemplate) {
        return null;
    }

    const { productList } = orderTemplate;
    const createdDate = productList?.CreatedBy?.OperationDateTime;
    const updatedDate = productList?.UpdatedBy?.OperationDateTime;
    const isModified = createdDate && updatedDate && updatedDate > createdDate;

    const operation = isModified ? productList?.UpdatedBy : productList?.CreatedBy;
    const actionLabel = isModified ? resources.orderTemplateModifiedText : resources.orderTemplateCreatedText;
    const operationDate = isModified ? updatedDate : createdDate;
    const formattedDate = operationDate && formatOperationDate ? ` ${formatOperationDate(operationDate)}` : '';
    const byText = resources.orderTemplateByText ? ` ${resources.orderTemplateByText}` : '';
    const actionText = `${actionLabel}${formattedDate}${byText}`;
    const operatorText = getOperatorText(operation, businessPartnerUser, resources, context);

    return (
        <div className={`organization-order-template ${isModified ? 'modified' : 'created'}`}>
            <span className='creator'>
                {actionText}
                {operatorText && <span>{` ${operatorText}`}</span>}
            </span>
        </div>
    );
};
